import { ReactNode, useRef, useState } from "react";
import { ResDataDelete } from "../shared/protocols/editor/PtlDataDelete";
import { Filter, SortOrder } from "../shared/protocols/editor/PtlDataFind";
import { ResDataInsert } from "../shared/protocols/editor/PtlDataInsert";
import {
  ResDataUpdate,
  UpdateData,
} from "../shared/protocols/editor/PtlDataUpdate";
import { dataCall } from "../tsrpc";

const DEFAUL_LIMIT = 20;

type DataProviderCacheEntry<T extends DataType> = {
  data: T[];
  hasMore: boolean;
  skip: number;
};

const dataProviderCache = new Map<string, DataProviderCacheEntry<any>>();

export interface DataType {
  _id: string | number;
  [key: string]: any;
}

export interface ContextType<T extends DataType> {
  data: T[];
  hasMore: boolean;
  get: GetHandler<T>;
  load: LoadHandler<T>;
  update: UpdateHandler;
  add: AddHandler<T>;
  remove: RemoveHandler<T>;
}

export type GetHandler<T extends DataType> = (
  filter?: Filter,
  sort?: { [key: string]: SortOrder },
  limit?: number,
  skip?: number,
  projection?: { [key: string]: 0 | 1 },
  random?: boolean
) => Promise<T[]>;
export type LoadHandler<T extends DataType> = (
  filter?: Filter,
  reset?: boolean
) => Promise<T[]>;
export type UpdateHandler = (
  filter: Filter,
  update: UpdateData,
  multi?: boolean
) => Promise<ResDataUpdate>;
export type AddHandler<T extends DataType> = (
  values: Partial<Omit<T, "_id">> | Partial<Omit<T, "_id">>[]
) => Promise<ResDataInsert>;
export type RemoveHandler<T extends DataType> = (
  ids: T["_id"] | T["_id"][]
) => Promise<ResDataDelete>;

export function DataProvider<T extends DataType>({
  context: Context,
  collection,
  sort = { _id: 1 },
  projection,
  limit = DEFAUL_LIMIT,
  cacheKey,
  children,
}: {
  context: React.Context<ContextType<T> | null>;
  collection: string;
  sort?: { [key: string]: SortOrder };
  projection?: { [key: string]: 0 | 1 };
  limit?: number;
  cacheKey?: string;
  children: ReactNode;
}) {
  const cached = cacheKey ? dataProviderCache.get(cacheKey) as DataProviderCacheEntry<T> | undefined : undefined;
  const [data, setData] = useState<T[]>(cached?.data ?? []);
  const [hasMore, setHasMore] = useState(cached?.hasMore ?? true);
  const [skip, setSkip] = useState(cached?.skip ?? 0);
  const loadGenerationRef = useRef(0);

  const writeCache = (nextData: T[], nextHasMore: boolean, nextSkip: number) => {
    if (!cacheKey) return;
    dataProviderCache.set(cacheKey, {
      data: nextData,
      hasMore: nextHasMore,
      skip: nextSkip,
    });
  };

  const get: GetHandler<T> = async (
    filter,
    sort,
    limit,
    skip,
    projection,
    random
  ) => {
    const res = await dataCall("editor/DataFind", {
      collection,
      filter,
      sort,
      skip,
      limit,
      projection,
      random,
    });
    if (res.isSucc) return res.res.data;
    else throw res.err;
  };

  const load: LoadHandler<T> = async (filter, reset = false) => {
    if (!hasMore && !reset) return [];
    const generation = reset
      ? loadGenerationRef.current + 1
      : loadGenerationRef.current;
    if (reset) loadGenerationRef.current = generation;
    let offset = skip;
    let requestFilter = filter;
    if (filter && Object.keys(filter as any).length === 1) {
      const key = Object.keys(filter as any)[0];
      const value = filter[key] as any;
      if (key === "_id" && Array.isArray(value?.$in)) {
        requestFilter = {
          ...filter,
          [key]: {
            ...value,
            $in: value.$in.slice(reset ? 0 : skip, reset ? limit : skip + limit),
          },
        };
        offset = 0;
      }
    }
    const res = await dataCall("editor/DataFind", {
      collection,
      filter: requestFilter,
      sort,
      skip: reset ? 0 : offset,
      limit,
      projection,
    });
    if (res.isSucc) {
      if (generation !== loadGenerationRef.current) return [];
      const nextHasMore = res.res.data.length === limit;
      setData((prev) => {
        const nextData = reset ? res.res.data : prev.concat(res.res.data);
        const nextSkip = reset ? res.res.data.length : prev.length + res.res.data.length;
        writeCache(nextData, nextHasMore, nextSkip);
        return nextData;
      });
      setHasMore(nextHasMore);
      setSkip((prev) => {
        const nextSkip = reset ? res.res.data.length : prev + res.res.data.length;
        return nextSkip;
      });
      return res.res.data;
    } else throw res.err;
  };

  const update: UpdateHandler = async (filter, update, multi = false) => {
    const res = await dataCall("editor/DataUpdate", {
      collection,
      filter,
      update,
      multi,
    });
    if (res.isSucc) {
      const resf = await dataCall("editor/DataFind", {
        collection,
        filter,
        sort,
        projection,
      });
      if (resf.isSucc)
        setData((prev) => {
          const nextData = prev.map((v) => resf.res.data.find((vf) => vf._id === v._id) || v);
          writeCache(nextData, hasMore, skip);
          return nextData;
        });
      return res.res;
    } else throw res.err;
  };

  const add: AddHandler<T> = async (values) => {
    const res = await dataCall("editor/DataInsert", {
      collection,
      data: values,
    });
    if (res.isSucc) {
      if (hasMore) return res.res;
      setData((prev) => {
        const nextData = prev.concat(
          Array.isArray(values)
            ? (values.map((v, i) => ({
                ...v,
                _id: res.res.insertedIds[i],
              })) as T[])
            : ({ ...values, _id: res.res.insertedIds[0] } as T),
        );
        const nextSkip = skip + (Array.isArray(values) ? values.length : 1);
        writeCache(nextData, hasMore, nextSkip);
        setSkip(nextSkip);
        return nextData;
      });
      return res.res;
    } else throw res.err;
  };

  const remove: RemoveHandler<T> = async (ids) => {
    const res = await dataCall("editor/DataDelete", {
      collection,
      filter: { _id: Array.isArray(ids) ? { $in: ids } : ids },
      multi: Array.isArray(ids),
    });
    if (res.isSucc) {
      setData((prev) => {
        const nextData = prev.filter((v) =>
          Array.isArray(ids) ? !ids.includes(v._id) : v._id !== ids
        );
        writeCache(nextData, hasMore, Math.min(skip, nextData.length));
        setSkip((prevSkip) => Math.min(prevSkip, nextData.length));
        return nextData;
      });
      return res.res;
    } else throw res.err;
  };

  return (
    <Context.Provider value={{ data, hasMore, get, load, update, add, remove }}>
      {children}
    </Context.Provider>
  );
}
